import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE } from "daedalus-core";
import { HOLE, SOLID, WATER } from "../collision/flags.ts";
import { diagonalUnits, latticeMove, offsetAfter, offsetAgainst, type Offsets } from "./true-point.ts";

/**
 * Deterministic movement over the collision grid (Playbook 7.2, ADR-029): integer sixteenths of a tile,
 * one axis at a time, the feet box sliding flush against whatever blocks it. Server and client prediction
 * run this same step.
 */

/**
 * Milliseconds per simulation tick (20 Hz).
 */
export const TICK_MS = 50;

/**
 * Sixteenths of a tile per tile; the wire and simulation unit.
 */
export const SIXTEENTHS = 16;

/**
 * Collision cells per tile in the grid the simulation reads: the compiled grid's 4 px cells (ADR-113 decision 2).
 */
export const CELLS_PER_TILE = COMPILED_COLLISION_CELLS_PER_TILE;

/**
 * Sixteenths per cell of the grid the simulation reads: movement, sight, the projectile's last clear cell,
 * `nearestStandable` and `restampThings` count in it.
 */
export const COLLISION_CELL = SIXTEENTHS / CELLS_PER_TILE;

/**
 * Sixteenths per authoring cell, the cell the document's collision and zone layers, activation cells, spawn areas
 * and a shrine's reach are painted in (ADR-113 decision 2): code that works in those cells reads this and never
 * `COLLISION_CELL`, which follows the compiled grid.
 */
export const AUTHORING_CELL = SIXTEENTHS / COLLISION_CELLS_PER_TILE;

/**
 * The longest whole step of a walk in sixteenths, `ceil(walk_speed / SPEED_UNITS)` of the shipped rules, which a
 * repository test holds it to: the bound a caller uses for the most a walking character covers in one step. A step's
 * real length is `latticeMove` of the rules' speed from the offset the character carries.
 */
export const WALK_SPEED = 3;

/**
 * Half the feet box's width in sixteenths: a 6 by 4 box (the feet's own width, a quarter tile tall),
 * ADR-029 as amended, whose bottom edge is the feet line so a character stops where their feet meet the
 * obstacle.
 */
export const FOOT_HALF_WIDTH = 3;

/**
 * The feet box's height in sixteenths, rising from the feet line.
 */
export const FOOT_HEIGHT = 4;

/**
 * Collision bits that stop movement in Phase 0.
 */
export const BLOCKING = SOLID | WATER | HOLE;

/**
 * Movement intent as the protocol carries it: four cardinals and four diagonals.
 */
export const Direction = { None: 0, Up: 1, Down: 2, Left: 3, Right: 4, UpLeft: 5, UpRight: 6, DownLeft: 7, DownRight: 8 } as const;
export type Direction = (typeof Direction)[keyof typeof Direction];

/**
 * Which way an entity looks, as the snapshot carries it.
 */
export const Facing = { Down: 0, Up: 1, Left: 2, Right: 3 } as const;
export type Facing = (typeof Facing)[keyof typeof Facing];

/**
 * What the step reads and writes: feet position in sixteenths and facing.
 */
export interface MovementState {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
}

/**
 * One map's whole collision grid, one byte per cell, with the value read beyond its edges. `beyond`, when present,
 * answers for a cell outside the grid in place of `outside`: the ground of a neighbouring map that a character of
 * this map may stand on (the hand band, ADR-147 decision 1). It is called only for cells outside the grid and
 * allocates nothing.
 */
export interface CollisionGrid {
  readonly width: number;
  readonly height: number;
  readonly cells: Readonly<Uint8Array>;
  readonly outside: number;
  readonly beyond?: ((cx: number, cy: number) => number) | undefined;
}

/**
 * Reports whether a value is a movement intent.
 *
 * @param value Candidate.
 * @returns True for 0 to 8.
 */
export function isDirection(value: number): value is Direction {
  return Number.isInteger(value) && value >= Direction.None && value <= Direction.DownRight;
}

/**
 * The intent held keys compose: opposite keys on one axis cancel, one axis alone is a cardinal, both a
 * diagonal.
 *
 * @param up Up held.
 * @param down Down held.
 * @param left Left held.
 * @param right Right held.
 * @returns The direction.
 */
export function composeDirection(up: boolean, down: boolean, left: boolean, right: boolean): Direction {
  const vertical = up === down ? 0 : up ? -1 : 1;
  const horizontal = left === right ? 0 : left ? -1 : 1;
  if (vertical === 0) {
    return horizontal === 0 ? Direction.None : horizontal < 0 ? Direction.Left : Direction.Right;
  }
  if (horizontal === 0) {
    return vertical < 0 ? Direction.Up : Direction.Down;
  }
  if (vertical < 0) {
    return horizontal < 0 ? Direction.UpLeft : Direction.UpRight;
  }
  return horizontal < 0 ? Direction.DownLeft : Direction.DownRight;
}

/**
 * The signed axis components of an intent, in unit steps.
 *
 * @param dir The intent.
 * @returns `dx` and `dy` in -1, 0 or 1.
 */
function axesOf(dir: Direction): { dx: number; dy: number } {
  const dx = dir === Direction.Right || dir === Direction.UpRight || dir === Direction.DownRight ? 1 : dir === Direction.Left || dir === Direction.UpLeft || dir === Direction.DownLeft ? -1 : 0;
  const dy = dir === Direction.Down || dir === Direction.DownLeft || dir === Direction.DownRight ? 1 : dir === Direction.Up || dir === Direction.UpLeft || dir === Direction.UpRight ? -1 : 0;
  return { dx, dy };
}

/**
 * The facing after a step: a cardinal faces its way; a diagonal keeps the current facing when it is one
 * of the diagonal's axes (adding a key never turns the character) and otherwise faces sideways, where a
 * four-way sprite reads best.
 *
 * @param facing Facing before the step.
 * @param dx Horizontal component, -1, 0 or 1.
 * @param dy Vertical component, -1, 0 or 1.
 * @returns The facing after.
 */
function facingAfter(facing: Facing, dx: number, dy: number): Facing {
  const horizontal = dx < 0 ? Facing.Left : Facing.Right;
  const vertical = dy < 0 ? Facing.Up : Facing.Down;
  if (dx === 0) {
    return vertical;
  }
  if (dy === 0) {
    return horizontal;
  }
  return facing === horizontal || facing === vertical ? facing : horizontal;
}

/**
 * Whether the feet box centred at a position overlaps a blocking cell.
 *
 * @param x Centre x in sixteenths.
 * @param y Centre y in sixteenths.
 * @param grid The grid.
 * @param mask Blocking bits.
 * @returns True when blocked.
 */
function blocked(x: number, y: number, grid: CollisionGrid, mask: number): boolean {
  const left = Math.floor((x - FOOT_HALF_WIDTH) / COLLISION_CELL);
  const right = Math.floor((x + FOOT_HALF_WIDTH - 1) / COLLISION_CELL);
  const top = Math.floor((y - FOOT_HEIGHT) / COLLISION_CELL);
  const bottom = Math.floor((y - 1) / COLLISION_CELL);
  for (let cy = top; cy <= bottom; cy += 1) {
    for (let cx = left; cx <= right; cx += 1) {
      const byte = cx < 0 || cy < 0 || cx >= grid.width || cy >= grid.height ? (grid.beyond === undefined ? grid.outside : grid.beyond(cx, cy)) : (grid.cells[cy * grid.width + cx] as number);
      if ((byte & mask) !== 0) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Whether the feet box can stand at a position.
 *
 * @param x Centre x in sixteenths.
 * @param y Centre y in sixteenths.
 * @param grid The grid.
 * @param mask Blocking bits; `BLOCKING` by default.
 * @returns True when no overlapped cell blocks.
 */
export function canStand(x: number, y: number, grid: CollisionGrid, mask: number = BLOCKING): boolean {
  return !blocked(x, y, grid, mask);
}

/**
 * Moves along one axis by a signed amount, sliding flush against the first blocking cell. Returns the
 * coordinate, never an object, so a tick allocates nothing.
 *
 * @param x Centre x.
 * @param y Centre y.
 * @param delta Signed movement in sixteenths.
 * @param horizontal True to move along x, false along y.
 * @param grid The grid.
 * @param mask Blocking bits.
 * @returns The furthest legal coordinate on the moved axis.
 */
function slideAxis(x: number, y: number, delta: number, horizontal: boolean, grid: CollisionGrid, mask: number): number {
  const start = horizontal ? x : y;
  if (delta === 0) {
    return start;
  }
  const target = start + delta;
  if (!(horizontal ? blocked(target, y, grid, mask) : blocked(x, target, grid, mask))) {
    return target;
  }
  const unit = Math.sign(delta);
  let legal = start;
  for (let i = 1; i <= Math.abs(delta); i += 1) {
    const candidate = start + unit * i;
    if (horizontal ? blocked(candidate, y, grid, mask) : blocked(x, candidate, grid, mask)) {
      break;
    }
    legal = candidate;
  }
  return legal;
}

/**
 * The facing a step along an intent ends with, wherever the step would land: what the map server gives a character
 * whose input it acknowledges without a step, so the page's reconcile keeps the facing it predicted.
 *
 * @param facing Facing before the step.
 * @param dir Movement intent.
 * @returns The facing after; `facing` itself for no intent.
 * @throws Error when the intent is not a direction.
 */
export function facingToward(facing: Facing, dir: Direction): Facing {
  if (!isDirection(dir)) {
    throw new Error(`${dir} is not a direction`);
  }
  if (dir === Direction.None) {
    return facing;
  }
  const axes = axesOf(dir);
  return facingAfter(facing, axes.dx, axes.dy);
}

/**
 * How many movement inputs after an act's own input keep the facing the act turned its caster to (ADR-073's amendment
 * "the way a displacement goes, and a swing keeps its facing"): a character's action strips (attack 1, attack 2 and the
 * dash) each play 500 ms, 10 ticks, and a page sends one input a tick, so the strip plays facing the way its blow lands
 * while the character walks on. Counted in inputs, not ticks, so the map server and the page's prediction hold the same
 * inputs whenever they arrive. `tests/swing-length.test.ts` pins it to the strips.
 */
export const SWING_HOLD_INPUTS = 10;

/**
 * The facing an input leaves while a swing may hold it: the facing before the input when the input is inside the hold
 * (its seq at or below `heldThrough`, an act's input plus `SWING_HOLD_INPUTS`), otherwise the facing its step gives.
 *
 * @param before The facing before the input.
 * @param stepped The facing the input's step or turn gives.
 * @param seq The input's seq.
 * @param heldThrough The last seq a swing holds, 0 for none.
 * @returns The facing after the input.
 */
export function heldFacing(before: Facing, stepped: Facing, seq: number, heldThrough: number): Facing {
  return seq <= heldThrough ? before : stepped;
}

/**
 * Advances an entity by one step of intent, X then Y, each axis sliding flush against blocking cells. Each axis of the
 * intent moves the length given: what moves by a known length calls it (a creature's whole step, the interpolator's clipping), and
 * a character's step, whose length its speed and its true point give, is `stepWalk`.
 *
 * @param state Position and facing before the step.
 * @param dir Movement intent.
 * @param grid The map's collision grid.
 * @param mask Blocking bits, `BLOCKING` for a character.
 * @param length Whole sixteenths moved along each axis of the intent, at least 0.
 * @returns The state after the step.
 * @throws Error when the position is not on the integer lattice or the intent is not a direction.
 */
export function stepMovement(state: MovementState, dir: Direction, grid: CollisionGrid, mask: number, length: number): MovementState {
  if (!Number.isInteger(state.x) || !Number.isInteger(state.y)) {
    throw new Error(`position (${state.x}, ${state.y}) is not in integer sixteenths`);
  }
  if (!isDirection(dir)) {
    throw new Error(`${dir} is not a direction`);
  }
  if (dir === Direction.None) {
    return state;
  }
  const axes = axesOf(dir);
  const x = slideAxis(state.x, state.y, axes.dx * length, true, grid, mask);
  const y = slideAxis(x, state.y, axes.dy * length, false, grid, mask);
  return { x, y, facing: facingAfter(state.facing, axes.dx, axes.dy) };
}

/**
 * Moves one axis of a walking character by a true move: the lattice coordinate goes to the one nearest the new true
 * coordinate, sliding flush against blocking cells, and the axis's offset keeps what is left. A move a wall stops short
 * holds the true point against the wall (`offsetAgainst`).
 *
 * @param x Feet x in sixteenths.
 * @param y Feet y.
 * @param move The true move in speed units, signed; 0 leaves the axis as it is.
 * @param horizontal True to move along x, false along y.
 * @param grid The grid.
 * @param mask Blocking bits.
 * @param offsets The character's offsets, whose moved axis is written.
 * @returns The coordinate on the moved axis after the move.
 */
function walkAxis(x: number, y: number, move: number, horizontal: boolean, grid: CollisionGrid, mask: number, offsets: Offsets): number {
  const start = horizontal ? x : y;
  if (move === 0) {
    return start;
  }
  const offset = horizontal ? offsets.x : offsets.y;
  const delta = latticeMove(offset, move);
  const end = slideAxis(x, y, delta, horizontal, grid, mask);
  const after = end === start + delta ? offsetAfter(offset, move) : offsetAgainst(move);
  if (horizontal) {
    offsets.x = after;
  } else {
    offsets.y = after;
  }
  return end;
}

/**
 * Advances a walking character by one step at a speed (ADR-116's amendment of 2026-10-07), X then Y, each axis sliding
 * flush against blocking cells as `stepMovement` slides it. The step moves the character's true point by the ruled
 * move, the speed along a cardinal's axis and the diagonal's share of it (`diagonalUnits`) along each axis of a
 * diagonal, and the lattice point follows to the nearest lattice point. So a step's length depends on the speed and on
 * the offsets the character carries and on nothing a page sends, an axis a wall holds leaves the other as even as a
 * walk, and no order of steps covers more ground than a straight walk at the speed.
 *
 * @param state Position and facing before the step.
 * @param dir Movement intent.
 * @param grid The map's collision grid.
 * @param mask Blocking bits, `BLOCKING` for a character.
 * @param units The speed in speed units, at least `SPEED_UNITS`.
 * @param offsets The character's offsets before the step, written with those after it; untouched by no intent.
 * @returns The state after the step.
 * @throws Error when the position is not on the integer lattice or the intent is not a direction.
 */
export function stepWalk(state: MovementState, dir: Direction, grid: CollisionGrid, mask: number, units: number, offsets: Offsets): MovementState {
  if (!Number.isInteger(state.x) || !Number.isInteger(state.y)) {
    throw new Error(`position (${state.x}, ${state.y}) is not in integer sixteenths`);
  }
  if (!isDirection(dir)) {
    throw new Error(`${dir} is not a direction`);
  }
  if (dir === Direction.None) {
    return state;
  }
  const axes = axesOf(dir);
  const move = axes.dx !== 0 && axes.dy !== 0 ? diagonalUnits(units) : units;
  const x = walkAxis(state.x, state.y, axes.dx * move, true, grid, mask, offsets);
  const y = walkAxis(x, state.y, axes.dy * move, false, grid, mask, offsets);
  return { x, y, facing: facingAfter(state.facing, axes.dx, axes.dy) };
}
